import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import type { Task } from '../types';
import { apiRequest } from '../utils/api';
import DateView, { type TaskStatus } from './tasks/DateView';
import WeekView from './tasks/WeekView';
import MonthView from './tasks/MonthView';

export default function Schedule() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [savingTask, setSavingTask] = useState(false);


  // Load tasks from database on mount
  useEffect(() => {
    const loadTasks = async () => {
      try {
        const response = await apiRequest('/tasks', { method: 'GET' });
        const tasksData = (response.data || []) as Task[];
        setTasks(tasksData);
      } catch (error) {
        console.error('Error loading tasks:', error);
        // Fallback to localStorage if database fails
        const savedTasks = localStorage.getItem('tasks');
        if (savedTasks) {
          try {
            setTasks(JSON.parse(savedTasks));
          } catch (parseError) {
            console.error('Error parsing localStorage tasks:', parseError);
          }
        }
      }
    };

    loadTasks();
  }, []);

  // Handle editing task (for Week and Month views)
  const handleEditTask = (_task: Task) => {
    // Editing is handled within each view component
    // WeekView and MonthView will need to implement their own edit dialogs
  };

  // Handle deleting task
  const handleDeleteTask = async (taskId: number) => {
    try {
      await apiRequest(`/tasks/${taskId}`, { method: 'DELETE' });
      setTasks(tasks.filter((task) => task.id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
      alert('Failed to delete task. Please try again.');
    }
  };

  // Handle moving task between columns
  const handleMoveTask = async (taskId: number, newStatus: TaskStatus) => {
    try {
      setSavingTask(true);
      const response = await apiRequest(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus }),
      });

      const updatedTask = response.data as Task;
      setTasks(
        tasks.map((task) =>
          task.id === taskId ? updatedTask : task
        )
      );
    } catch (error) {
      console.error('Error updating task status:', error);
      alert('Failed to update task status. Please try again.');
    } finally {
      setSavingTask(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box>
      {/* Loading Modal */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={savingTask}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Saving task...</Typography>
        </Box>
      </Backdrop>

      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary">
          Schedule Management
        </Typography>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 2 }}>
          Schedule
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="schedule view tabs">
          <Tab label="Month" />
          <Tab label="Week" />
          <Tab label="Date" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && (
        <MonthView
          tasks={tasks}
          onTasksChange={setTasks}
          onEditTask={handleEditTask}
          onDeleteTask={handleDeleteTask}
          onMoveTask={handleMoveTask}
          setSavingTask={setSavingTask}
        />
      )}
      {currentTab === 1 && (
        <WeekView
          tasks={tasks}
          onTasksChange={setTasks}
          onEditTask={handleEditTask}
          onDeleteTask={handleDeleteTask}
          onMoveTask={handleMoveTask}
          setSavingTask={setSavingTask}
        />
      )}
      {currentTab === 2 && (
        <DateView
          tasks={tasks}
          onTasksChange={setTasks}
          setSavingTask={setSavingTask}
        />
      )}
    </Box>
  );
}
